import { ICON__chevron_down, ICON__heart_solid, ICON__image_solid, ICON__star_solid, ICON__user_solid } from '@/assets/icons_fa';
import { UI_PRIMITIVE__button } from '@/components/ui/UI_PRIMITIVE__button';
import { UI_PRIMITIVE__dropdown_menu } from '@/components/ui/dropdown/UI_PRIMITIVE__dropdown_menu';
import { UI_PRIMITIVE__dropdown_menu_content } from '@/components/ui/dropdown/UI_PRIMITIVE__dropdown_menu_content';
import { UI_PRIMITIVE__dropdown_menu_item } from '@/components/ui/dropdown/UI_PRIMITIVE__dropdown_menu_item';
import { UI_PRIMITIVE__dropdown_menu_trigger } from '@/components/ui/dropdown/UI_PRIMITIVE__dropdown_menu_trigger';
import NotifierBorder from '@/components/ui/notifier/notifier-border';
import { ENUM__component_type } from '@/enums/ENUM__component_type';
import { HOOK__notifier } from '@/hooks/HOOK__notifier';
import { use_dispatch } from '@/hooks/_base/use_dispatch';
import { use_slice_search, use_slice_workspace } from '@/hooks/store/use_slices';
import i18n_page_search from '@/locales/keys/i18n_page_search.json';
import { BarWorkspaceEntityOrBarLine } from '@/pages/_base/views/BarWorkspaceEntityOrBarLine';
import { PLACEHOLDER_bar_line } from '@/pages/_base/views/PLACEHOLDER_bar_line';
import { ENUM__i18n_namespace } from '@/services/i18n/ENUM__i18n_namespace';
import { remote_search_update } from '@/state/search/remote_search_update';
import { not_implemented_type } from '@shared/asserts/utils_asserts';
import { ENUM__workspace_entity } from '@shared/dto/+workspace/workspace_entity/_enums/ENUM__workspace_entity';
import { ENUM__workspace_entity_feature } from '@shared/dto/+workspace/workspace_entity/_enums/ENUM__workspace_entity_feature';
import { ESearchScope } from '@shared/dto/search/ESearchScope';
import { useTranslation } from 'react-i18next';

export function SScopeDropdown() {
  const dispatch = use_dispatch();

  const { t } = useTranslation(ENUM__i18n_namespace.PAGE_SEARCH);

  const { search_current } = use_slice_search(['search_current']);
  const { current_workspace_user } = use_slice_workspace(['current_workspace_user']);

  const component_id = `${ENUM__component_type.PAGE_SEARCH_SCOPE_DROPDOWN_TRIGGER}_${search_current._id}`;
  const notifier = HOOK__notifier(component_id, ENUM__workspace_entity.ENTITY_SEARCH);

  const trigger_id = 'dropdown_search_scope';

  /*
   * EVENTS
   */

  async function on_item_selected(value: string) {
    if (search_current.scope == value) return;

    let search_new = search_current.clone({ scope: value });

    await dispatch(remote_search_update(search_new));

    await notifier.send_action();
  }

  /*
   * METHODS
   */

  function user_can_view() {
    if (search_current.is_stub()) return false;

    return search_current.is_shared || current_workspace_user.is_feature_pertmitted(ENUM__workspace_entity_feature.ENTITY_SEARCH_VIEW);
  }

  function is_disabled() {
    return !current_workspace_user.is_feature_pertmitted(ENUM__workspace_entity_feature.ENTITY_SEARCH_UPDATE);
  }

  function render_items() {
    return Object.values(ESearchScope).map((value: string) => {
      return (
        <UI_PRIMITIVE__dropdown_menu_item
          key={value.toString()}
          value={value}
          label={t(i18n_page_search.sidebar.dropdown.scope.items[value])}
          values_selected={[search_current.scope]}
          class_name_hovered_selected="bg-accent"
          {...{ on_item_selected }}
          className={`w-full rounded-lg transition-colors duration-300 cursor-pointer text-left flex justify-between
         `}
        />
      );
    });
  }

  /*
   * RENDERS
   */

  function render_icon() {
    let class_name_base = '!w-7 !h-7 !text-component-item-foreground';

    let out = <ICON__image_solid className={class_name_base} />;

    switch (search_current.scope) {
      case ESearchScope.ALL:
        out = <ICON__image_solid className={class_name_base} />;
        break;

      case ESearchScope.WORKSPACE:
        out = <ICON__user_solid className={class_name_base} />;
        break;
      case ESearchScope.WORKSPACE_FAVORITES:
        out = <ICON__heart_solid className={class_name_base} />;
        break;
      case ESearchScope.ALL_FAVORITES:
        out = <ICON__star_solid className={class_name_base} />;
        break;
      default:
        not_implemented_type(search_current.scope);
    }

    return out;
  }
  /*
   * HTML
   */

  if (!user_can_view()) return <PLACEHOLDER_bar_line />;

  function render_view() {
    return (
      <>
        <UI_PRIMITIVE__dropdown_menu>
          <UI_PRIMITIVE__dropdown_menu_trigger asChild>
            <UI_PRIMITIVE__button id={trigger_id} className="relative w-full justify-start h-9" variant="outline-on-bar" disabled={is_disabled()}>
              <NotifierBorder notifier={notifier} />
              <span className="w-full text-left">{t(i18n_page_search.sidebar.dropdown.scope.title)}:</span>

              <span className="max-w-32 min-w-28 text-right">{t(i18n_page_search.sidebar.dropdown.scope.items[search_current.scope])}</span>
              {render_icon()}
              <ICON__chevron_down className="!w-6 !h-6 !text-component-item-foreground" />
            </UI_PRIMITIVE__button>
          </UI_PRIMITIVE__dropdown_menu_trigger>
          <UI_PRIMITIVE__dropdown_menu_content trigger_id={trigger_id} className="rounded-lg">
            {render_items()}
          </UI_PRIMITIVE__dropdown_menu_content>
        </UI_PRIMITIVE__dropdown_menu>
      </>
    );
  }

  return <BarWorkspaceEntityOrBarLine view={render_view()} entity={search_current} required_permission={ENUM__workspace_entity_feature.ENTITY_SEARCH_VIEW} />;
}
